/**
 * Recruiting Notes API Service
 *
 * Fetches recruiting notes (org-scoped, recency-ordered) for the Latest Notes
 * web feed. Recruiting notes are primarily created on mobile; web consumes
 * them read-only here.
 */

import { get, post } from './api';
import type { PaginatedResponse, RecruitingNoteResponse } from '../types/api';

export type ListRecruitingNotesParams = {
  page?: number;
  pageSize?: number;
  /** When true, only return notes assigned to a prospect (prospectId != null). */
  assignedOnly?: boolean;
};

export const listRecruitingNotes = (
  params: ListRecruitingNotesParams = {},
): Promise<PaginatedResponse<RecruitingNoteResponse>> => {
  const { page = 1, pageSize = 20, assignedOnly = false } = params;

  const queryParams = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  if (assignedOnly) {
    queryParams.set('assignedOnly', 'true');
  }

  return get<PaginatedResponse<RecruitingNoteResponse>>(
    `/api/recruiting-notes?${queryParams.toString()}`,
  );
};

/**
 * Fetch all recruiting notes for a single prospect, ordered most-recent-first.
 * Used by the Docs tab on the prospect viewer to surface mobile-authored notes.
 */
export const getRecruitingNotesByProspect = (
  prospectId: string,
): Promise<RecruitingNoteResponse[]> =>
  get<RecruitingNoteResponse[]>(`/api/prospects/${prospectId}/recruiting-notes`);

/**
 * Fetch a single recruiting note by id. Used by viewer components to refresh
 * a note's signed URLs when they are about to expire (or have already 403'd),
 * since the image/drawing signed URLs are time-bound.
 */
export const refetchRecruitingNote = (
  id: string,
): Promise<RecruitingNoteResponse> =>
  get<RecruitingNoteResponse>(`/api/recruiting-notes/${id}`);

/**
 * Fetch the single active team-scoped recruiting note for a team, if one
 * exists. Team annotations are org-shared (one canonical annotated roster per
 * team) and authored on iPad; web consumes them read-only here, rendering the
 * baked composite JPEG (`imageUrl`) — never the proprietary `.pkdrawing`.
 *
 * The returned note has a null `prospectId`/`prospectName` (it is team-scoped),
 * so all consumers must tolerate the absence of a prospect.
 *
 * Throws an `ApiError` with `statusCode === 404` when the team has no active
 * annotation — callers should treat that as "no annotation yet", not an error.
 */
export const getActiveTeamRecruitingNote = (
  teamId: string,
): Promise<RecruitingNoteResponse> =>
  get<RecruitingNoteResponse>(
    `/api/recruiting-notes/team/${teamId}/active`,
  );

export type CreateRecruitingNotePayload = {
  content: string;
  title?: string;
  prospectId?: string;
};

/**
 * Create a text recruiting note from web. Mirrors the mobile create endpoint
 * but is restricted to text-mode notes — handwritten capture stays mobile-only.
 */
export const createRecruitingNote = (
  payload: CreateRecruitingNotePayload,
): Promise<RecruitingNoteResponse> =>
  post<RecruitingNoteResponse>('/api/recruiting-notes', {
    content: payload.content,
    title: payload.title,
    noteType: 'text',
    prospectId: payload.prospectId,
    isActiveNote: false,
  });
